import React from "react";
import { Link } from "react-router-dom";

const BASE_FONT_SIZE = 1.5;          // rem – scales all text
const CLOVER_SIZE    = 10;        // vw – base clover emoji size, scales with screen width
const CLOVER_COLOR   = "#1a5c2a"; // shamrock tint
const BG_BASE_COLOR  = "#f5f0d0"; // cream background

const f = (n) => `${+(n * BASE_FONT_SIZE).toFixed(4)}rem`;
const c = (n) => `${+(n * CLOVER_SIZE).toFixed(4)}vw`;

export default function StPatricksPromo() {
  const cloverRotations = React.useMemo(
    () => Array.from({ length: 10 }, () => Math.floor(Math.random() * 360)),
    []
  );

  return (
    <div className="stpatricks-promo" aria-label="St. Patrick's Week Dinner Special">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Outfit:wght@400;600;700&display=swap');

        @keyframes cloverFloat {
          0%   { transform: translateY(0) rotate(0deg); opacity: 0.7; }
          50%  { transform: translateY(-18px) rotate(12deg); opacity: 0.9; }
          100% { transform: translateY(0) rotate(0deg); opacity: 0.7; }
        }

        @keyframes cloverDrift {
          0%   { transform: translate(0, 0) rotate(0deg) scale(1); }
          33%  { transform: translate(8px, -12px) rotate(8deg) scale(1.05); }
          66%  { transform: translate(-6px, -6px) rotate(-5deg) scale(0.97); }
          100% { transform: translate(0, 0) rotate(0deg) scale(1); }
        }

        @keyframes softPulse {
          0%, 100% { box-shadow: inset 0 0 80px rgba(34, 90, 34, 0.08); }
          50%      { box-shadow: inset 0 0 120px rgba(34, 90, 34, 0.16); }
        }

        .stpatricks-promo {
          --sp-cream: ${BG_BASE_COLOR};
          --sp-green-dark: ${CLOVER_COLOR};
          --sp-green-mid: #3a8a4a;
          --sp-green-light: #b5d8a0;
          --sp-green-pale: #d4eac8;
          --sp-gold: #c8a84e;
          --sp-text: #1a3a1a;

          position: relative;
          width: 100%;
          min-height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background: linear-gradient(160deg, var(--sp-cream) 0%, #e8e0b8 40%, var(--sp-green-pale) 100%);
          overflow: hidden;
          animation: softPulse 5s ease-in-out infinite;
          font-family: 'Outfit', sans-serif;
        }

        .stpatricks-promo__clovers {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
          overflow: hidden;
        }

        .stpatricks-promo__clover {
          position: absolute;
          font-size: ${c(1)};
          color: ${CLOVER_COLOR};
          opacity: 0.5;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.08));
          rotate: var(--r, 0deg);
        }

        .stpatricks-promo__clover:nth-child(1)  { top: 2%;  left: 3%;  font-size: ${c(1.4)};  animation: cloverFloat 6s ease-in-out infinite; }
        .stpatricks-promo__clover:nth-child(2)  { top: 8%;  right: 5%; font-size: ${c(1.6)};  animation: cloverDrift 8s ease-in-out infinite; opacity: 0.4; }
        .stpatricks-promo__clover:nth-child(3)  { top: 25%; left: 6%;  font-size: ${c(0.8)};  animation: cloverFloat 7s ease-in-out infinite 1s; }
        .stpatricks-promo__clover:nth-child(4)  { bottom: 30%; right: 4%; font-size: ${c(1.2)}; animation: cloverDrift 9s ease-in-out infinite 0.5s; }
        .stpatricks-promo__clover:nth-child(5)  { bottom: 8%;  left: 8%;  font-size: ${c(1.52)}; animation: cloverFloat 7.5s ease-in-out infinite 2s; opacity: 0.45; }
        .stpatricks-promo__clover:nth-child(6)  { bottom: 5%;  right: 8%; font-size: ${c(1.28)}; animation: cloverDrift 6.5s ease-in-out infinite 1.5s; }
        .stpatricks-promo__clover:nth-child(7)  { top: 50%;  left: 2%;  font-size: ${c(0.88)}; animation: cloverFloat 8.5s ease-in-out infinite 0.8s; opacity: 0.35; }
        .stpatricks-promo__clover:nth-child(8)  { top: 15%; left: 50%; font-size: ${c(0.8)};  animation: cloverDrift 10s ease-in-out infinite 3s; opacity: 0.3; }
        .stpatricks-promo__clover:nth-child(9)  { bottom: 20%; left: 45%; font-size: ${c(1)};  animation: cloverFloat 9s ease-in-out infinite 1.2s; opacity: 0.25; }
        .stpatricks-promo__clover:nth-child(10) { top: 5%;  left: 35%; font-size: ${c(0.72)}; animation: cloverDrift 7s ease-in-out infinite 2.5s; opacity: 0.35; }

        .stpatricks-promo__content {
          position: relative;
          z-index: 2;
          text-align: center;
          padding: ${f(2)} ${f(1.5)};
          max-width: 620px;
          color: var(--sp-text);
        }

        .stpatricks-promo__eyebrow {
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-size: ${f(1.35)};
          color: var(--sp-green-dark);
          margin: 0 0 ${f(0.25)};
        }

        .stpatricks-promo__title {
          font-family: 'Playfair Display', serif;
          font-weight: 900;
          font-size: ${f(2.4)};
          color: var(--sp-green-dark);
          margin: 0 0 ${f(0.6)};
          line-height: 1.1;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .stpatricks-promo__desc {
          font-size: ${f(0.85)};
          line-height: 1.5;
          color: var(--sp-text);
          margin: 0 0 ${f(0.4)};
          opacity: 0.85;
        }

        .stpatricks-promo__price {
          font-family: 'Playfair Display', serif;
          font-weight: 900;
          font-size: ${f(2)};
          color: var(--sp-green-dark);
          margin: ${f(0.5)} 0;
        }

        .stpatricks-promo__dates {
          font-weight: 700;
          font-size: ${f(0.95)};
          text-transform: uppercase;
          letter-spacing: 2px;
          color: var(--sp-green-dark);
          margin: ${f(0.3)} 0 ${f(0.6)};
          padding: ${f(0.35)} ${f(1)};
          border: 2px solid var(--sp-green-mid);
          border-radius: 6px;
          display: inline-block;
          background: rgba(255,255,255,0.5);
        }

        .stpatricks-promo__cta-line {
          font-size: ${f(1)};
          font-weight: 600;
          color: var(--sp-text);
          margin: ${f(0.6)} 0 ${f(0.8)};
        }

        .stpatricks-promo__btn,
        .stpatricks-promo__btn:visited,
        .stpatricks-promo__btn:active {
          display: inline-block;
          background: var(--sp-green-dark);
          color: #fff !important;
          padding: ${f(0.75)} ${f(2)};
          border-radius: 8px;
          font-family: 'Outfit', sans-serif;
          font-weight: 700;
          font-size: ${f(1)};
          text-transform: uppercase;
          letter-spacing: 2px;
          text-decoration: none;
          border: 2px solid var(--sp-gold);
          transition: background 200ms ease, transform 150ms ease;
        }

        .stpatricks-promo__btn:hover {
          background: var(--sp-green-mid);
          color: #fff !important;
          transform: scale(1.04);
        }

        @media (max-width: 600px) {
          .stpatricks-promo__content { padding: 2.5vw 3vw; }
          .stpatricks-promo__eyebrow { font-size: 3.5vw; margin-bottom: 0.5vw; }
          .stpatricks-promo__title { font-size: 5.5vw; margin-bottom: 1.5vw; }
          .stpatricks-promo__desc { font-size: 2.8vw; line-height: 1.35; margin-bottom: 1vw; }
          .stpatricks-promo__price { font-size: 5.5vw; margin: 1vw 0; }
          .stpatricks-promo__dates { font-size: 2.6vw; letter-spacing: 1px; padding: 1vw 2.5vw; margin: 0.5vw 0 1.5vw; }
          .stpatricks-promo__cta-line { font-size: 3vw; margin: 1.5vw 0 2vw; }
          .stpatricks-promo__btn { padding: 1.5vw 4vw; font-size: 2.8vw; }
        }
      `}</style>

      <div className="stpatricks-promo__clovers" aria-hidden="true">
        {cloverRotations.map((deg, i) => (
          <span key={i} className="stpatricks-promo__clover" style={{ '--r': `${deg}deg` }}>&#9752;</span>
        ))}
      </div>

      <div className="stpatricks-promo__content">
        <p className="stpatricks-promo__eyebrow">St. Patrick's Week Dinner Special</p>
        <h3 className="stpatricks-promo__title">Corned Beef &amp; Cabbage</h3>

        <p className="stpatricks-promo__desc">
          Slow-braised corned beef, sliced thick and tender. Served with braised cabbage, potatoes &amp; sweet carrots.
        </p>
        <p className="stpatricks-promo__desc">
          Includes your choice of cup of soup or fresh garden salad.
          <br />
          Served with warm bread &amp; butter.
        </p>
        <p className="stpatricks-promo__desc">
          Finished with our homemade bread pudding topped with Jack Daniel's whiskey sauce.
        </p>

        <div className="stpatricks-promo__price">$28.99</div>

        <div className="stpatricks-promo__dates">March 16 – 22, 2026</div>

        <p className="stpatricks-promo__cta-line">Dine In or Order Online</p>

        <Link to="/burbank/order" className="stpatricks-promo__btn">
          Order Online
        </Link>
      </div>
    </div>
  );
}
